import { useCallback, useEffect, useState } from 'react';
import { CalendarCheck, KeyRound, LogOut, Mail, MessageSquare, PhoneForwarded, Plug, ShieldCheck, Unplug } from 'lucide-react';
import { AdminCalls } from './AdminCalls';
import { toast } from 'sonner';
import { Toaster } from './ui/sonner';
import { API } from '../lib/site';
import './AdminPage.css';

const request = async (path, token, body) => {
  const response = await fetch(`${API}${path}`, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Something went wrong. Please try again.');
  return data;
};

const StatusPill = ({ ok, label, testid, readyLabel = 'Connected' }) => (
  <span className={`admin-pill ${ok ? 'is-ok' : ''}`} data-testid={testid}>{ok ? readyLabel : 'Not set up'} · {label}</span>
);

export const AdminPage = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem('jos_admin_token') || '');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ sendgrid_api_key: '', sendgrid_sender: '', google_client_id: '', google_client_secret: '', google_calendar_id: '', twilio_account_sid: '', twilio_auth_token: '', twilio_from_number: '', oncall_general: '', oncall_criminal: '', oncall_immigration: '', oncall_additional: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async active => {
    try {
      const data = await request('/admin/settings', active);
      setStatus(data);
      setForm(current => ({ ...current, sendgrid_sender: data.sendgrid_sender, google_client_id: data.google_client_id, google_calendar_id: data.google_calendar_id, twilio_account_sid: data.twilio_account_sid, twilio_from_number: data.twilio_from_number, oncall_general: data.oncall_general, oncall_criminal: data.oncall_criminal, oncall_immigration: data.oncall_immigration, oncall_additional: data.oncall_additional }));
    } catch {
      sessionStorage.removeItem('jos_admin_token');
      setToken('');
    }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  useEffect(() => {
    const outcome = new URLSearchParams(window.location.search).get('google');
    if (outcome === 'connected') toast.success('Google Calendar is connected.');
    if (outcome === 'denied') toast.error('Google sign-in was cancelled.');
    if (outcome === 'failed') toast.error('Google connection failed. Please check the Client ID and Secret.');
    if (outcome) window.history.replaceState({}, '', '/admin');
  }, []);

  const login = async event => {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      const data = await request('/admin/login', '', { password });
      sessionStorage.setItem('jos_admin_token', data.token);
      setToken(data.token);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const save = async event => {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      const updates = {};
      for (const [key, value] of Object.entries(form)) if (value.trim()) updates[key] = value.trim();
      const data = await request('/admin/settings', token, updates);
      setStatus(data);
      setForm(current => ({ ...current, sendgrid_api_key: '', google_client_secret: '', twilio_auth_token: '' }));
      toast.success('Settings saved securely.');
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const connect = async () => {
    setBusy(true); setError('');
    try {
      const data = await request(`/admin/google/auth-url?origin=${encodeURIComponent(window.location.origin)}`, token);
      window.location.href = data.url;
    } catch (err) { setError(err.message); setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true); setError('');
    try {
      const data = await request('/admin/google/disconnect', token, {});
      setStatus(data);
      toast.success('Google Calendar disconnected.');
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const logout = () => { sessionStorage.removeItem('jos_admin_token'); setToken(''); setStatus(null); };

  const field = (key, label, testid, type = 'text', placeholder = '', autoComplete = 'off') => (
    <label className="admin-field">{label}
      <input type={type} value={form[key]} autoComplete={autoComplete} placeholder={placeholder}
        onChange={event => setForm(current => ({ ...current, [key]: event.target.value }))} data-testid={testid} />
    </label>
  );

  if (!token) {
    return <main className="admin-page" data-testid="admin-page">
      <Toaster position="top-center" />
      <form className="admin-card admin-login" onSubmit={login} data-testid="admin-login-form">
        <KeyRound size={22} strokeWidth={1.5} />
        <h1>Jos Legal Group</h1>
        <p>Private settings area. Please enter the admin password.</p>
        <input type="password" value={password} onChange={event => setPassword(event.target.value)}
          placeholder="Admin password" autoComplete="current-password" required data-testid="admin-password-input" />
        {error && <p className="admin-error" role="alert" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="admin-login-submit">{busy ? 'Checking…' : 'Sign in'}</button>
      </form>
    </main>;
  }

  return <main className="admin-page" data-testid="admin-page">
    <Toaster position="top-center" />
    <div className="admin-card admin-panel">
      <div className="admin-head">
        <div><h1>Receptionist integrations</h1>
          <p>Connect the services Jenny uses to book consultations and send emails. Secrets are stored encrypted and never shown again.</p></div>
        <button className="admin-ghost" onClick={logout} data-testid="admin-logout"><LogOut size={14} /> Sign out</button>
      </div>
      {error && <p className="admin-error" role="alert" data-testid="admin-error">{error}</p>}
      <form onSubmit={save} data-testid="admin-settings-form">
        <section>
          <h2><Mail size={15} /> SendGrid — call transcripts & appointment emails</h2>
          <StatusPill ok={status?.sendgrid_configured} readyLabel="Configured" label={status?.sendgrid_configured ? `sender ${status.sendgrid_sender}` : 'emails off'} testid="sendgrid-status" />
          <p className="admin-hint" data-testid="call-transcript-email-info">Once SendGrid is configured, completed calls automatically email Jenny’s summary, full transcript and saved callback details to <strong>{status?.firm_notify_email}</strong>. No Google Calendar connection is needed for call emails.</p>
          <p className="admin-hint" data-testid="call-transcript-email-activation">New calls with the updated email notice can wait for connection for up to 24 hours. Deleted, expired and older calls without this consent are not sent. Temporary rejections are retried automatically; uncertain submissions are not resent to avoid duplicates.</p>
          {field('sendgrid_api_key', 'API key', 'sendgrid-key-input', 'password', status?.sendgrid_configured ? 'Saved — enter a new key to replace it' : 'SG.xxxxxxxxxxxx')}
          {field('sendgrid_sender', 'Verified sender email', 'sendgrid-sender-input', 'email', 'you@yourdomain.com')}
          <p className="admin-hint" data-testid="sendgrid-setup-help">Create a key at SendGrid → Settings → API Keys (Mail Send permission), and verify this sender under Settings → Sender Authentication. Saved credentials are not proof of delivery; check SendGrid Email Activity for rejected or unconfirmed messages.</p>
        </section>
        <section>
          <h2><CalendarCheck size={15} /> Google Calendar — 30-minute bookings</h2>
          <StatusPill ok={status?.google_connected} label={status?.google_connected ? `calendar ${status.google_calendar_id}${status.google_account_email ? ` · ${status.google_account_email}` : ''}` : 'booking off'} testid="google-status" />
          {field('google_client_id', 'OAuth Client ID', 'google-client-id-input', 'text', status?.google_oauth_configured ? 'Saved — enter a new ID to replace it' : 'xxxx.apps.googleusercontent.com')}
          {field('google_client_secret', 'OAuth Client Secret', 'google-client-secret-input', 'password', status?.google_oauth_configured ? 'Saved — enter a new secret to replace it' : 'GOCSPX-xxxx')}
          {field('google_calendar_id', 'Calendar ID (usually your Gmail address, or “primary”)', 'google-calendar-id-input')}
          <p className="admin-hint">In Google Cloud Console: enable the Google Calendar API, create an OAuth “Web application” client, and add this exact redirect URI:</p>
          <code className="admin-code" data-testid="redirect-uri-hint">{`${window.location.origin}/api/admin/google/callback`}</code>
        </section>
        <section>
          <h2><MessageSquare size={15} /> Twilio — text follow-ups & staff alerts</h2>
          <StatusPill ok={status?.sms_configured} readyLabel="Configured" label={status?.sms_configured ? `from ${status.twilio_from_number}` : 'texts off'} testid="twilio-status" />
          <p className="admin-hint" data-testid="twilio-info">With Twilio connected, Jenny texts a recap and guide link to visitors who say yes, sends booking confirmations and one-hour reminders by text, and alerts the on-call number below the moment an urgent matter is detected. Every visitor text ends with an opt-out line; STOP replies are honoured permanently.</p>
          {field('twilio_account_sid', 'Account SID', 'twilio-sid-input', 'text', 'ACxxxxxxxxxxxxxxxx')}
          {field('twilio_auth_token', 'Auth token', 'twilio-token-input', 'password', status?.sms_configured ? 'Saved — enter a new token to replace it' : 'Auth token from the Twilio Console')}
          {field('twilio_from_number', 'Twilio phone number (sending number)', 'twilio-from-input', 'tel', '+1 949 555 0100')}
          <p className="admin-hint" data-testid="twilio-setup-help">In the Twilio Console copy the Account SID and Auth Token, buy or use a US number, and set its Messaging webhook to:</p>
          <code className="admin-code" data-testid="sms-webhook-hint">{`${window.location.origin}/api/sms/inbound`}</code>
          <p className="admin-hint">US carriers require A2P 10DLC registration for business texting; until approved, texts to some numbers may be filtered.</p>
        </section>
        <section>
          <h2><PhoneForwarded size={15} /> On-call numbers for urgent alerts</h2>
          <p className="admin-hint" data-testid="oncall-info">Urgent alerts are texted to the practice’s on-call number. Defaults are the practice’s public line; change them to a staff mobile.</p>
          {field('oncall_general', 'Family / general', 'oncall-general-input', 'tel')}
          {field('oncall_criminal', 'Criminal defense', 'oncall-criminal-input', 'tel')}
          {field('oncall_immigration', 'Immigration', 'oncall-immigration-input', 'tel')}
          {field('oncall_additional', 'Personal injury · Estate · Employment · Bankruptcy · Property', 'oncall-additional-input', 'tel')}
        </section>
        <button type="submit" disabled={busy} data-testid="admin-save-settings"><ShieldCheck size={15} /> {busy ? 'Saving…' : 'Save settings'}</button>
      </form>
      <section className="admin-connect">
        <h2><Plug size={15} /> Authorize the calendar</h2>
        <p>After saving your Client ID and Secret, connect the Google account that owns the calendar. Booking notifications go to <strong>{status?.firm_notify_email}</strong>.</p>
        <div className="admin-connect-actions">
          <button onClick={connect} disabled={busy || !status?.google_oauth_configured} data-testid="google-connect-button">
            <Plug size={15} /> {status?.google_connected ? 'Reconnect Google Calendar' : 'Connect Google Calendar'}</button>
          {status?.google_connected && <button className="admin-ghost" onClick={disconnect} disabled={busy} data-testid="google-disconnect-button"><Unplug size={14} /> Disconnect</button>}
        </div>
        {!status?.google_oauth_configured && <p className="admin-hint" data-testid="connect-blocked-hint">Save your Google Client ID and Secret first.</p>}
      </section>
      <AdminCalls token={token} request={request} />
    </div>
  </main>;
};
